import React, { useEffect, useState } from "react";
import { Grid3x3, CheckCircle2, ChevronRight } from "lucide-react";
import api from "../../lib/api";

const cell = (pct) => {
  if (pct >= 70) return { bg: "rgba(16,185,129,0.16)", fg: "#6ee7b7", br: "rgba(16,185,129,0.35)" };
  if (pct >= 45) return { bg: "rgba(245,158,11,0.16)", fg: "#fcd34d", br: "rgba(245,158,11,0.35)" };
  return { bg: "rgba(244,63,94,0.16)", fg: "#fca5a5", br: "rgba(244,63,94,0.35)" };
};

export default function PortfolioRegulatoryHeatmap({ onOpenClient }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/enterprise360/regulator-coverage/portfolio")
      .then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data || !data.clients?.length) return null;
  return (
    <div data-testid="portfolio-regulatory-heatmap" className="border ek-line rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Grid3x3 size={16} className="text-[var(--e360-primary)]" />
          <h3 className="text-[14px] font-semibold ek-text">Portfolio regulatory heatmap</h3>
        </div>
        <div className="text-[12px] ek-text-muted">
          {data.clients.length} engagements · portfolio avg{" "}
          <b className="text-[var(--e360-primary)]">{data.portfolio_avg}%</b> · weakest first
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="ek-text-muted">
              <th className="text-left font-medium py-1.5 pr-3">Engagement</th>
              {data.regimes.map((r) => (
                <th key={r.id} className="font-medium px-2 py-1.5 text-center whitespace-nowrap">{r.short}</th>
              ))}
              <th className="font-medium px-2 py-1.5 text-center">Avg</th>
            </tr>
          </thead>
          <tbody>
            {data.clients.map((c) => (
              <tr key={c.id} data-testid={`heatmap-row-${c.id}`}
                className="border-t ek-line hover:bg-zinc-50 cursor-pointer"
                onClick={() => onOpenClient?.(c.id)}>
                <td className="py-1.5 pr-3 ek-text-soft font-medium whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">{c.name} <ChevronRight size={12} className="ek-text-muted" /></span>
                </td>
                {data.regimes.map((r) => {
                  const v = c.cells[r.id];
                  if (!v) return <td key={r.id} className="px-2 py-1.5 text-center ek-text-muted">—</td>;
                  const s = cell(v.readiness_pct);
                  return (
                    <td key={r.id} className="px-1.5 py-1.5 text-center">
                      <button data-testid={`heatmap-cell-${c.id}-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); onOpenClient?.(c.id, { tab: "standards", regime: r.id }); }}
                        title={`Open ${r.short} pack for ${c.name}`}
                        className="inline-flex items-center justify-center gap-1 rounded px-2 py-1 font-semibold min-w-[52px] hover:brightness-110 transition-[filter]"
                        style={{ backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.br}` }}>
                        {v.readiness_pct}%
                        {v.signed_off && <CheckCircle2 size={11} />}
                      </button>
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-center font-bold" style={{ color: cell(c.average_pct).fg }}>{c.average_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] ek-text-muted">
        <span>Readiness:</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(16,185,129,0.5)" }} /> ≥70</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(245,158,11,0.5)" }} /> 45–69</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "rgba(244,63,94,0.5)" }} /> &lt;45</span>
        <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> signed off</span>
      </div>
    </div>
  );
}
